"use client";

import { type DialogProps } from "@radix-ui/react-dialog";
import { Command as CommandPrimitive } from "cmdk";
import escapeRegExp from "lodash/escapeRegExp";
import * as React from "react";
import { Dialog, DialogContent } from "@/components/molecules/Dialog";
import { cn } from "@/lib/utils";

export type CommandProps = React.ComponentPropsWithoutRef<
  typeof CommandPrimitive
> & {
  children?: React.ReactNode;
  className?: string;
  disabled?: boolean;
  errorMsg?: string;
  fullWidth?: boolean;
  open?: boolean;
  placeholder?: React.ReactNode;
  prefix?: React.ReactNode;

  readOnly?: boolean;
  suffix?: React.ReactNode;
  testId?: string;
  onOpenChange?: (open: boolean) => void;
  onValueChange?: (value: string) => void;
};

const Command = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive> & {
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    errorMsg?: string;
  }
>(
  (
    {
      className,
      onOpenChange,
      errorMsg: _errorMsg, // purposefully not passing errorMsg to CommandPrimitive, as it doesn't have such a prop and will error if we pass it down
      ...props
    },
    ref,
  ) => {
    const handleFocus = () => onOpenChange?.(true);
    const handleBlur = () => onOpenChange?.(false);

    return (
      <div className="tw-relative">
        <CommandPrimitive
          ref={ref}
          className={cn(
            "tw-flex tw-h-full tw-w-full tw-flex-col tw-overflow-hidden tw-rounded-md tw-bg-base-white",
            className,
          )}
          onFocusCapture={handleFocus}
          onBlurCapture={handleBlur}
          {...props}
        />
      </div>
    );
  },
);
Command.displayName = CommandPrimitive.displayName;

const CommandDialog = ({ children, ...props }: DialogProps) => {
  return (
    <Dialog {...props}>
      <DialogContent className="tw-overflow-hidden tw-p-0 tw-shadow-lg">
        <Command className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5">
          {children}
        </Command>
      </DialogContent>
    </Dialog>
  );
};

const CommandInput = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Input>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input> & {
    fullWidth?: boolean;
    errorMsg?: string;
  }
>(({ className, fullWidth, errorMsg, ...props }, ref) => {
  return (
    <div
      className={cn(
        "tw-flex tw-items-center tw-px-3",
        fullWidth && "tw-w-full",
      )}
    >
      <CommandPrimitive.Input
        ref={ref}
        className={cn(
          "tw-h-40 tw-px-12 tw-py-8 tw-border tw-border-gray-400 tw-rounded-4 tw-bg-base-white tw-text-gray-900 tw-body-2",
          "placeholder:tw-text-gray-600 placeholder:tw-body-2",
          "focus:tw-border-gray-900",
          "disabled:tw-text-gray-500 disabled:placeholder:tw-text-gray-500",
          "[appearance:textfield] [&::-webkit-outer-spin-button]:tw-appearance-none [&::-webkit-inner-spin-button]:tw-appearance-none",
          "tw-w-full",
          errorMsg && "tw-border-red-800 focus:tw-border-red-800",
          className,
        )}
        {...props}
      />
    </div>
  );
});

CommandInput.displayName = CommandPrimitive.Input.displayName;

const CommandList = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.List> & {
    open?: boolean;
    className?: string;
  }
>(({ className, open, ...props }, ref) => {
  return (
    <>
      {open ? (
        <CommandPrimitive.List
          ref={ref}
          className={cn(
            "tw-max-h-[300px] tw-overflow-y-auto tw-overflow-x-hidden tw-shadow-md tw-rounded-4",
            "tw-absolute tw-z-50 tw-bg-base-white",
            "tw-left-0 tw-right-0",
            "tw-translate-y-1",
            className,
          )}
          {...props}
        />
      ) : null}
    </>
  );
});

CommandList.displayName = CommandPrimitive.List.displayName;

const CommandEmpty = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Empty>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Empty>
>((props, ref) => (
  <CommandPrimitive.Empty
    ref={ref}
    className="tw-py-6 tw-text-center tw-text-sm"
    {...props}
  />
));

CommandEmpty.displayName = CommandPrimitive.Empty.displayName;

const CommandGroup = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Group>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Group>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Group
    ref={ref}
    className={cn(
      "tw-overflow-hidden tw-p-1",
      "[&_[cmdk-group-heading]]:tw-px-3 [&_[cmdk-group-heading]]:tw-py-2",
      "[&_[cmdk-group-heading]]:tw-text-gray-400 [&_[cmdk-group-heading]]:tw-text-base",
      className,
    )}
    {...props}
  />
));

CommandGroup.displayName = CommandPrimitive.Group.displayName;

const CommandSeparator = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Separator
    ref={ref}
    className={cn("tw-h-px tw-bg-gray-300 tw-my-2", className)}
    {...props}
  />
));
CommandSeparator.displayName = CommandPrimitive.Separator.displayName;

const CommandItem = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Item> & {
    isExactMatch?: boolean;
    searchValue?: string;
    children?: React.ReactNode;
  }
>(({ className, isExactMatch, searchValue = "", children, ...props }, ref) => {
  const highlightText = (text: string) => {
    const searchString = String(searchValue || "");
    if (!searchString || typeof text !== "string") return text;

    const parts = text.split(
      new RegExp(`(${escapeRegExp(searchString)})`, "gi"),
    );
    return (
      <span className="tw-whitespace-pre">
        {parts.map((part, i) =>
          part.toLowerCase() === searchValue.toLowerCase() ? (
            <span key={i} className="tw-text-gray-900 tw-font-medium">
              {part}
            </span>
          ) : (
            <span key={i} className="tw-text-gray-600">
              {part}
            </span>
          ),
        )}
      </span>
    );
  };

  return (
    <CommandPrimitive.Item
      ref={ref}
      className={cn(
        "tw-relative tw-flex tw-cursor-pointer tw-select-none tw-items-center tw-px-3 tw-py-3",
        "tw-text-base",
        "tw-data-[disabled=true]:tw-pointer-events-none tw-data-[disabled=true]:tw-opacity-50",
        "tw-py-8 tw-px-16 tw-border-b-[1px] tw-border-gray-400",
        "hover:tw-bg-gray-100 data-[selected=true]:tw-bg-gray-100",
        isExactMatch && "tw-text-purple-700",
        className,
      )}
      {...props}
    >
      {typeof children === "string" ? highlightText(children) : children}
    </CommandPrimitive.Item>
  );
});

CommandItem.displayName = CommandPrimitive.Item.displayName;

const CommandShortcut = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => {
  return (
    <span
      className={cn(
        "tw-ml-auto tw-text-xs tw-tracking-widest tw-text-muted-foreground",
        className,
      )}
      {...props}
    />
  );
};
CommandShortcut.displayName = "CommandShortcut";

export {
  Command,
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
  CommandSeparator,
};
